// Centralized cart controller for Concocina theme
// Wraps Shopify Ajax Cart API + optional Section Rendering API
// and exposes a small evented API on window.ConcocinaCart.

(function () {
  var global = window || {};

  var rootUrl =
    global.Shopify && global.Shopify.routes && global.Shopify.routes.root
      ? global.Shopify.routes.root
      : '/';

  function safeJson(response) {
    if (!response.ok) {
      var error = new Error('Cart request failed');
      error.status = response.status;
      return response
        .json()
        .catch(function () {
          return null;
        })
        .then(function (data) {
          if (data && data.description) {
            error.description = data.description;
          }
          if (data && data.message) {
            error.message = data.message;
          }
          error.body = data;
          throw error;
        });
    }
    return response.json();
  }

  var subscribers = {};

  function emit(eventName, payload) {
    if (subscribers[eventName]) {
      subscribers[eventName].forEach(function (handler) {
        try {
          handler(payload);
        } catch (e) {
          console.error('ConcocinaCart listener error for', eventName, e);
        }
      });
    }
    try {
      document.dispatchEvent(
        new CustomEvent('concocina:' + eventName, { detail: payload })
      );
    } catch (e) {
      // Ignore environments without CustomEvent
    }
  }

  function on(eventName, handler) {
    if (!eventName || typeof handler !== 'function') return;
    if (!subscribers[eventName]) subscribers[eventName] = [];
    subscribers[eventName].push(handler);
  }

  function off(eventName, handler) {
    if (!eventName || !subscribers[eventName]) return;
    if (!handler) {
      subscribers[eventName] = [];
      return;
    }
    subscribers[eventName] = subscribers[eventName].filter(function (fn) {
      return fn !== handler;
    });
  }

  function getCartJson() {
    emit('cart:request', { type: 'getCart' });
    return fetch(rootUrl + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' }
    })
      .then(safeJson)
      .then(function (cart) {
        emit('cart:updated', { cart: cart, source: 'getCart' });
        announceCartUpdate(cart, 'getCart');
        return cart;
      })
      .catch(function (error) {
        emit('cart:error', { error: error, source: 'getCart' });
        throw error;
      });
  }

  var lineQueues = {};

  function enqueueLineChange(lineKey, task) {
    if (!lineQueues[lineKey]) {
      lineQueues[lineKey] = Promise.resolve();
    }
    var queue = lineQueues[lineKey].then(function () {
      return task();
    });
    lineQueues[lineKey] = queue.finally(function () {
      if (lineQueues[lineKey] === queue) {
        lineQueues[lineKey] = null;
      }
    });
    return queue;
  }

  function changeLineByKey(lineKey, quantity, options) {
    options = options || {};
    if (!lineKey) {
      return Promise.reject(new Error('Missing line item key'));
    }
    var qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 0) qty = 0;

    return enqueueLineChange(lineKey, function () {
      emit('cart:request', {
        type: 'changeLine',
        key: lineKey,
        quantity: qty,
        source: options.source || null
      });

      return fetch(rootUrl + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: qty })
      })
        .then(safeJson)
        .then(function (cart) {
          emit('cart:updated', {
            cart: cart,
            source: options.source || 'changeLine'
          });
          announceCartUpdate(cart, options.source || 'changeLine');
          return cart;
        })
        .catch(function (error) {
          emit('cart:error', {
            error: error,
            source: options.source || 'changeLine',
            key: lineKey
          });
          throw error;
        });
    });
  }

  function removeLineByKey(lineKey, options) {
    return changeLineByKey(lineKey, 0, options);
  }

  function addFromForm(form, options) {
    options = options || {};
    if (!form) {
      return Promise.reject(new Error('Missing form element'));
    }
    var formData = new FormData(form);

    emit('cart:request', { type: 'addFromForm', source: options.source || null });

    return fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      body: formData
    })
      .then(safeJson)
      .then(function () {
        return getCartJson().then(function (cart) {
          announceCartUpdate(cart, options.source || 'addFromForm');
          return cart;
        });
      })
      .catch(function (error) {
        emit('cart:error', {
          error: error,
          source: options.source || 'addFromForm'
        });
        throw error;
      });
  }

  function refreshSections(config) {
    config = config || {};
    var sections = config.sections || [];
    if (!sections.length) return Promise.resolve({});

    var url = config.url || (global.location && global.location.href);
    if (!url) return Promise.resolve({});

    var origin = global.location && global.location.origin;
    var baseUrl;
    try {
      baseUrl = origin ? new URL(url, origin) : new URL(url);
    } catch (e) {
      // Fallback: try with rootUrl if it's an absolute URL
      if (rootUrl && /^https?:/i.test(rootUrl)) {
        baseUrl = new URL(url, rootUrl);
      } else {
        throw e;
      }
    }

    emit('cart:request', {
      type: 'refreshSections',
      sections: sections
    });

    // Single-section HTML mode: use ?section_id and wrap the HTML into a map.
    if (sections.length === 1) {
      var sectionId = sections[0];
      var requestUrl = new URL(baseUrl.toString());
      requestUrl.search = '';
      requestUrl.searchParams.set('section_id', sectionId);

      return fetch(requestUrl.toString(), {
        method: 'GET',
        headers: {
          Accept: 'text/html'
        }
      })
        .then(function (response) {
          if (!response.ok) {
            var error = new Error('Section request failed');
            error.status = response.status;
            throw error;
          }
          return response.text();
        })
        .then(function (htmlText) {
          var map = {};
          map[sectionId] = htmlText;

          // Apply updated cart section HTML into the DOM when available.
          try {
            applyCartSectionHtml(sectionId, htmlText);
          } catch (e) {
            // Swallow DOM update errors so cart JSON APIs still work.
          }

          emit('cart:sections:updated', {
            sections: map,
            requested: sections
          });
          return map;
        })
        .catch(function (error) {
          emit('cart:error', {
            error: error,
            source: 'refreshSections',
            sections: sections
          });
          throw error;
        });
    }

    // Future: multi-section mode using ?sections=; for now, fall back to JSON map if available.
    var multiUrl = new URL(baseUrl.toString());
    multiUrl.search = '';
    multiUrl.searchParams.set('sections', sections.join(','));

    return fetch(multiUrl.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(safeJson)
      .then(function (htmlMap) {
        emit('cart:sections:updated', {
          sections: htmlMap,
          requested: sections
        });
        return htmlMap;
      })
      .catch(function (error) {
        emit('cart:error', {
          error: error,
          source: 'refreshSections',
          sections: sections
        });
        throw error;
      });
  }

  // Apply updated cart section HTML and re-bind cart-page behavior.
  function applyCartSectionHtml(sectionId, htmlText) {
    if (typeof DOMParser === 'undefined' || typeof document === 'undefined') return;

    var parser = new DOMParser();
    var doc = parser.parseFromString(htmlText, 'text/html');
    var newSection = doc.querySelector('.cart-section');
    if (!newSection) return;

    var selector = '.cart-section';
    if (sectionId) {
      selector += '[data-section-id="' + sectionId + '"]';
    }

    var currentSection =
      document.querySelector(selector) || document.querySelector('.cart-section');

    if (currentSection && currentSection.parentNode) {
      currentSection.parentNode.replaceChild(newSection, currentSection);
      // Re-wire cart-page events on the fresh section.
      initCartPage(newSection);
    }
  }

  // Centralised cart-page behavior (quantity changes + removes).
  function initCartPage(root) {
    if (typeof document === 'undefined') return;

    var section;
    if (root && root.classList && root.classList.contains('cart-section')) {
      section = root;
    } else {
      section = document.querySelector('.cart-section');
    }

    if (!section) return;

    var form = section.querySelector('.cart-section__form');
    if (!form) return;

    var sectionId = section.getAttribute('data-section-id');

    function showCartError(msg) {
      var inner = section.querySelector('.cart-section__inner');
      if (!inner) return;
      var el = inner.querySelector('[data-cart-page-error]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    function refreshCartSection() {
      // If we don't know the sectionId or cannot use Section Rendering,
      // fall back to updating totals from the cart JSON response.
      if (!global.ConcocinaCart || !global.ConcocinaCart.refreshSections || !sectionId) {
        getCartJson()
          .then(function (cart) {
            try {
              applyCartJsonToCartPage(section, cart);
            } catch (e) {
            }
          })
          .catch(function () {
            // Swallow JSON fallback errors; cart UI will remain as-is.
          });

        return;
      }

      global.ConcocinaCart
        .refreshSections({
          sections: [sectionId],
          url: global.location && global.location.href
        })
        .catch(function (err) {
          if (err && err.status === 429) {
            showCartError(
              'Demasiadas solicitudes. Espera un momento y vuelve a intentar.'
            );
          }
        });
    }

    form.addEventListener('change', function (event) {
      var target = event.target;
      if (
        !target ||
        !target.classList ||
        !target.classList.contains('cart-section__qty-input')
      )
        return;

      var row = target.closest('.cart-section__item');
      if (!row) return;
      if (row.getAttribute('data-updating') === 'true') return;

      var key = row.getAttribute('data-key');
      if (!key) return;

      var qty = Math.max(1, parseInt(target.value, 10) || 1);
      row.setAttribute('data-updating', 'true');
      row.setAttribute('aria-busy', 'true');
      showCartError('');

      changeLineByKey(key, qty, { source: 'cart-page' })
        .then(function (cart) {
          // Keep the cart drawer in sync with cart-page mutations.
          if (
            global.ConcocinaCart &&
            typeof global.ConcocinaCart.renderDrawerFromCart === 'function' &&
            cart
          ) {
            global.ConcocinaCart.renderDrawerFromCart(cart);
          }
          refreshCartSection();
        })
        .catch(function (err) {
          if (err && err.status === 429) {
            showCartError(
              'Demasiadas solicitudes. Espera un momento y vuelve a intentar.'
            );
          }
        })
        .finally(function () {
          row.removeAttribute('data-updating');
          row.removeAttribute('aria-busy');
        });
    });

    form.addEventListener('click', function (event) {
      var removeLink =
        event.target && event.target.closest
          ? event.target.closest('.cart-section__remove')
          : null;
      if (!removeLink) return;

      event.preventDefault();

      var row = removeLink.closest('.cart-section__item');
      if (!row) return;
      if (row.getAttribute('data-removing') === 'true') return;

      var key = row.getAttribute('data-key');
      if (!key) return;
      row.setAttribute('data-removing', 'true');
      row.setAttribute('aria-busy', 'true');
      showCartError('');

      changeLineByKey(key, 0, { source: 'cart-page' })
        .then(function (cart) {
          // Keep the cart drawer in sync when removing from the cart page.
          if (
            global.ConcocinaCart &&
            typeof global.ConcocinaCart.renderDrawerFromCart === 'function' &&
            cart
          ) {
            global.ConcocinaCart.renderDrawerFromCart(cart);
          }
          refreshCartSection();
        })
        .catch(function (err) {
          if (err && err.status === 429) {
            showCartError(
              'Demasiadas solicitudes. Espera un momento y vuelve a intentar.'
            );
          }
        })
        .finally(function () {
          row.removeAttribute('data-removing');
          row.removeAttribute('aria-busy');
        });
    });
  }

  // Update cart page totals and line-item totals using cart JSON from Shopify.
  function applyCartJsonToCartPage(section, cart) {
    if (!section || !cart || !cart.items) return;

    var form = section.querySelector('.cart-section__form');
    var currencyAttr =
      (form && form.getAttribute('data-currency')) || cart.currency || 'USD';

    function formatMoneyFromCart(amount) {
      if (
        global.Shopify &&
        typeof global.Shopify.formatMoney === 'function' &&
        global.theme &&
        global.theme.moneyFormat
      ) {
        try {
          return global.Shopify.formatMoney(amount, global.theme.moneyFormat);
        } catch (e) {
          // fall through to Intl
        }
      }
      var lang =
        (document.documentElement && document.documentElement.lang) || 'es-CL';
      try {
        return (amount / 100).toLocaleString(lang, {
          style: 'currency',
          currency: currencyAttr
        });
      } catch (e) {
        return String(amount / 100);
      }
    }

    // Update line item totals.
    var rows = section.querySelectorAll('.cart-section__item');
    rows.forEach(function (row) {
      var key = row.getAttribute('data-key');
      if (!key) return;
      var item = cart.items.find(function (it) {
        return it.key === key;
      });
      if (!item) return;
      var lineEl = row.querySelector('[data-line-total]');
      if (!lineEl) return;
      lineEl.textContent = formatMoneyFromCart(item.final_line_price);
    });

    // Update cart subtotal.
    var subtotalEl = section.querySelector('[data-cart-subtotal]');
    if (subtotalEl) {
      subtotalEl.textContent = formatMoneyFromCart(cart.total_price);
    }
  }

  // Announce cart changes via the live region section.
  function announceCartUpdate(cart, source) {
    try {
      if (!cart || typeof document === 'undefined') return;
      var container = document.getElementById('cart-live-region-text');
      if (!container) return;
      var status = container.querySelector('.cart-live-region-text__status');
      if (!status) return;

      var count = cart.item_count || 0;
      var total = cart.total_price || 0;
      var lang =
        (document.documentElement && document.documentElement.lang) || 'es-CL';
      var currency =
        (cart.currency && cart.currency) ||
        (global.Shopify &&
          global.Shopify.currency &&
          global.Shopify.currency.active) ||
        'CLP';

      var totalFormatted;
      try {
        totalFormatted = (total / 100).toLocaleString(lang, {
          style: 'currency',
          currency: currency
        });
      } catch (e) {
        totalFormatted = String(total / 100);
      }

      var message =
        count === 0
          ? 'Carrito vacío.'
          : 'Carrito actualizado. ' +
            count +
            ' producto' +
            (count === 1 ? '' : 's') +
            ', total ' +
            totalFormatted +
            '.';

      status.textContent = message;
    } catch (e) {
      // Do not break cart behavior if announcement fails.
    }
  }

  var api = global.ConcocinaCart || {};

  api.getCart = getCartJson;
  api.changeLineByKey = changeLineByKey;
  api.removeLineByKey = removeLineByKey;
  api.addFromForm = addFromForm;
  api.refreshSections = refreshSections;
  api.on = on;
  api.off = off;

  api.changeLine = api.changeLine || function (payload) {
    if (!payload) return Promise.reject(new Error('Missing payload'));
    return changeLineByKey(payload.id, payload.quantity, {
      source: payload.source || 'changeLine'
    });
  };

  api.addFormToCart = api.addFormToCart || function (form) {
    return addFromForm(form, { source: 'addFormToCart' });
  };

  global.ConcocinaCart = api;

  // Initialise cart-page behavior on first load.
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('DOMContentLoaded', function () {
      try {
        initCartPage();
      } catch (e) {
        // Swallow errors to avoid breaking the rest of the theme.
      }
    });
  }
})();

